/**
 * StintSense — deleting an account (#398). Pure helpers for the Settings card and the
 * sign-in panel; the rules themselves are the server's (`services/account_deletion.py`).
 *
 * The design council on #398 fixed the order: what is deleted, what stays, what blocks,
 * shown BEFORE anything is typed; the server's 409 is only the fallback for a teammate
 * joining in the meantime.
 */

/** The exact words the server compares. The client normalises what was typed (phone
 *  keyboards capitalise the first letter) and sends this literal. */
export const CONFIRM_PHRASE = "delete my account";

/** Whether destruction clears the driver's entry in retained `.ibt` files (#398 PR B).
 *  The Settings card and Privacy page choose their sentence from this, so the copy never
 *  claims what the code doesn't do. On since 2026-10-06: PR B deployed (1793f81) and
 *  proven on copies of all 56 stored files in production (stripped, verified,
 *  telemetry identical, idempotent). */
export const IBT_NAMES_STRIPPED = true;

export function phraseMatches(typed: string): boolean {
  return typed.trim().toLowerCase().replace(/\s+/g, " ") === CONFIRM_PHRASE;
}

export interface TeamForDeletion {
  id: string;
  name: string;
  myRole?: "owner" | "admin" | "member" | null;
  memberCount?: number;
}

export interface DeletionPlan {
  /** You own it alone: deleted with all its runs. */
  deleted: TeamForDeletion[];
  /** Your runs stay, shown as "Former driver". */
  stays: TeamForDeletion[];
  /** You are its only owner and it has other members: blocks the deletion. */
  blocks: TeamForDeletion[];
}

/** `otherOwners` is, per team id, how many owners there are besides you; a team you
 *  own with other members is unknown until it is fetched, and is never guessed. */
export function planDeletion(
  teams: TeamForDeletion[],
  otherOwners: Record<string, number | undefined>,
): DeletionPlan & { unknown: TeamForDeletion[] } {
  const plan = { deleted: [], stays: [], blocks: [], unknown: [] } as DeletionPlan & { unknown: TeamForDeletion[] };
  for (const t of teams) {
    const alone = (t.memberCount ?? 1) <= 1;
    if (t.myRole === "owner" && alone) plan.deleted.push(t);
    else if (t.myRole === "owner") {
      const others = otherOwners[t.id];
      if (others === undefined) plan.unknown.push(t);
      else if (others === 0) plan.blocks.push(t);
      else plan.stays.push(t);
    } else plan.stays.push(t);
  }
  return plan;
}

/** "19 October 2026": one written-out date, never a countdown (council). */
export function formatDeletionDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

type DeletionFields = { code?: string; deletionScheduledFor?: string; teams?: { id: string; name: string }[] };

/** The machine fields of a deletion error body, read from `ApiError.body`: top-level
 *  keys beside a string `detail` (the contract agreed on #398), or nested in an object
 *  `detail` as an older server sent them. */
export function deletionDetail(body: unknown): DeletionFields {
  if (!body || typeof body !== "object") return {};
  const b = body as DeletionFields & { detail?: unknown };
  if (typeof b.code === "string") return { code: b.code, deletionScheduledFor: b.deletionScheduledFor, teams: b.teams };
  return b.detail && typeof b.detail === "object" ? (b.detail as DeletionFields) : {};
}
